import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { useAudit } from "@/components/AuditContext";
import { ACTU_ARTICLES } from "@/lib/actualitesData";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

// Affiche le texte entre `accents graves` comme une commande.
const withCode = (str) =>
  str.split("`").map((part, i) =>
    i % 2 === 1 ? (
      <code key={i} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.9em] text-navy break-all">
        {part}
      </code>
    ) : (
      part
    )
  );

export default function ActualitesArticle({ slug }) {
  const { openAudit } = useAudit();
  const article = ACTU_ARTICLES[slug];

  if (!article) return null;

  return (
    <>
      <Seo title={article.title} description={article.metaDescription} path={`/actualites/${slug}`} />

      <section className="bg-navy grain relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <Link to="/actualites" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-amber-500 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Retour aux actualités
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-medium text-amber-500">
              <span>{article.eyebrow}</span>
              <span className="text-slate-500">•</span>
              <time dateTime={article.date} className="text-slate-400 tracking-normal normal-case">{formatDate(article.date)}</time>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white text-balance">
              {article.title}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              {article.excerpt}
            </p>
          </Reveal>
          {article.author && (
            <Reveal delay={0.16}>
              <p className="mt-6 text-sm text-slate-400">Par {article.author}</p>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 space-y-14">
          {article.sections.map((s, i) => (
            <Reveal key={s.heading} delay={i * 0.05}>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-navy">{s.heading}</h2>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">{s.text}</p>
                {s.points && (
                  <ul className="mt-5 space-y-3">
                    {s.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-lg text-slate-600 leading-relaxed">
                        <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                        <span>{withCode(point)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.steps && (
                  <ol className="mt-5 space-y-3">
                    {s.steps.map((step, n) => (
                      <li key={step} className="flex items-start gap-4 text-lg text-slate-600 leading-relaxed">
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
                          {n + 1}
                        </span>
                        <span className="min-w-0">{withCode(step)}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {(article.umboSolution || article.summary) && (
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            {article.umboSolution && (
              <Reveal>
                <div className="rounded-2xl bg-white border border-slate-200 p-8 md:p-10">
                  <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Ce que fait Umbo</span>
                  <p className="mt-4 text-lg text-slate-600 leading-relaxed">{article.umboSolution}</p>
                </div>
              </Reveal>
            )}

            {article.summary && (
              <Reveal delay={0.05}>
                <div className="mt-10">
                  <h3 className="font-display text-xl font-semibold text-navy">En bref</h3>
                  <ul className="mt-5 space-y-3">
                    {article.summary.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-slate-600 leading-relaxed">
                        <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">
              Parlons de votre situation, sans engagement.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <CTAButton size="lg" withArrow className="mt-10" onClick={openAudit} data-testid="actu-article-audit-cta">
              Demander un audit gratuit
            </CTAButton>
          </Reveal>
        </div>
      </section>
    </>
  );
}
